import { ArrowRight, Delete, Lock, ShieldCheck, Smartphone, UserRound } from "lucide-react";
import { useCallback, useEffect, useState } from "react";

import { Button } from "@/components/ui";
import { cn } from "@/lib/cn";
import { useAuthStore } from "@/stores/authStore";

import { ApiError } from "@/api/client";
import { deviceRepository, type Terminal } from "@/repositories/deviceRepository";

import { AuthError } from "./AuthError";
import { PairTerminal } from "./PairTerminal";
import { useDeviceToken } from "../useDeviceToken";

/**
 * The bottom row is clear · 0 · delete, and the two ⌫ glyphs do different jobs.
 *
 * The design showed the same icon twice, which the reference implementation
 * spotted and split: the left one clears the whole PIN, the right removes a
 * digit. That is the better reading. A cashier who has mistyped usually wants to
 * start over — reaching for one key four times to do it is the annoyance — and
 * putting *something* within reach of either hand still holds, because a till is
 * worked one-handed while the other is on the goods.
 *
 * They are labelled differently for screen readers, which is the only way the
 * difference is otherwise discoverable.
 */
const KEYS = [
  "1",
  "2",
  "3",
  "4",
  "5",
  "6",
  "7",
  "8",
  "9",
  "clear",
  "0",
  "backspace",
] as const;

// Must match apps/accounts/services/pin.py. A PIN is 4 to 6 digits — the form
// previously hardcoded 4 and submitted the moment the fourth was pressed, which
// meant anyone with a longer PIN could never sign in and would burn through the
// five-attempt lockout without ever seeing why.
const PIN_MIN_LENGTH = 4;
const PIN_MAX_LENGTH = 6;

/**
 * Staff code and PIN, at a till.
 *
 * The on-screen keypad is not decoration. A POS terminal is used standing up,
 * often one-handed, frequently with no physical keyboard. The keys are large for
 * the same reason the Pay button is.
 *
 * A physical numeric keypad still works — many desktop tills have one — because
 * digits, Backspace and Enter are handled at the document level.
 */
export function PinLoginForm() {
  const [staffCode, setStaffCode] = useState("");
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const [terminal, setTerminal] = useState<Terminal | null>(null);
  const [forgot, setForgot] = useState(false);
  const [lastSync, setLastSync] = useState("never");
  // The real thing, not a decoration. A cashier looking at this before a shift
  // is asking whether the shop is connected, and an indicator that always says
  // "Online" is worse than none — they would stop believing the rest of it.
  const [online, setOnline] = useState(() => navigator.onLine);

  const login = useAuthStore((s) => s.loginWithPin);
  const error = useAuthStore((s) => s.error);
  const { deviceToken, isPaired, refresh } = useDeviceToken();

  // Who works here, so a cashier taps their name instead of remembering a code.
  // Failing quietly is deliberate: the staff-code field below always works, and
  // a terminal that cannot reach the server must still be able to open.
  useEffect(() => {
    const update = () => setOnline(navigator.onLine);
    window.addEventListener("online", update);
    window.addEventListener("offline", update);
    return () => {
      window.removeEventListener("online", update);
      window.removeEventListener("offline", update);
    };
  }, []);

  // "Did yesterday's takings reach the server?" — answered before anybody signs
  // in, because that is when a shopkeeper wonders. Read from local sync state
  // rather than the network: the answer must be truthful with no signal.
  useEffect(() => {
    void deviceRepository.lastSyncAt().then((at) => {
      if (!at) return;
      const minutes = Math.round((Date.now() - new Date(at).getTime()) / 60000);
      setLastSync(
        minutes < 2
          ? "just now"
          : minutes < 60
            ? `${minutes} minutes ago`
            : new Date(at).toLocaleString("en-ZA", {
                day: "numeric",
                month: "short",
                hour: "2-digit",
                minute: "2-digit",
              }),
      );
    });
  }, []);

  useEffect(() => {
    if (!deviceToken) return;
    let live = true;
    void deviceRepository.terminal(deviceToken).then((found) => {
      if (live) setTerminal(found);
    });
    return () => {
      live = false;
    };
  }, [deviceToken]);

  const complete = staffCode.trim().length > 0 && pin.length >= PIN_MIN_LENGTH;

  const submit = useCallback(async () => {
    if (!complete || busy) return;

    setBusy(true);
    try {
      await login(staffCode.trim().toUpperCase(), pin, deviceToken ?? "");
    } catch (cause) {
      // The server does not recognise this terminal. Holding on to the token
      // would leave a cashier tapping a keypad that can never succeed — which is
      // exactly what a database reset produced: the browser still held a token
      // for a device that no longer existed, so the app called itself paired and
      // offered the wrong screen. Forget it and ask for a pairing code instead.
      if (cause instanceof ApiError && cause.code === "DEVICE_NOT_REGISTERED") {
        deviceRepository.forgetToken();
        refresh();
      }
      // The store holds the message. Clear the PIN so the next attempt starts
      // clean rather than appending to a wrong one.
      setPin("");
    } finally {
      setBusy(false);
    }
  }, [busy, complete, deviceToken, login, pin, refresh, staffCode]);

  const press = useCallback((key: string) => {
    if (key === "clear") {
      setPin("");
      return;
    }
    if (key === "backspace") {
      setPin((current) => current.slice(0, -1));
      return;
    }
    setPin((current) => (current.length >= PIN_MAX_LENGTH ? current : current + key));
  }, []);

  // A physical keypad drives the same state as the on-screen one.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      // Do not swallow typing in the staff-code field.
      if (target?.tagName === "INPUT") return;

      if (/^[0-9]$/.test(event.key)) {
        press(event.key);
      } else if (event.key === "Backspace") {
        event.preventDefault();
        press("backspace");
      } else if (event.key === "Escape") {
        event.preventDefault();
        press("clear");
      } else if (event.key === "Enter") {
        event.preventDefault();
        void submit();
      }
    };

    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [press, submit]);

  if (!isPaired) {
    return <PairTerminal onPaired={refresh} />;
  }

  // Four placeholders until the PIN is longer, so the field communicates the
  // minimum without implying six digits are required.
  const slots = Math.max(PIN_MIN_LENGTH, pin.length);

  const roster = terminal?.staff ?? [];

  return (
    <div className="space-y-4">
      {/* ── Which shop, and which counter ───────────────────────────────── */}
      {terminal && (
        <div className="text-center">
          <p className="text-[1.0625rem] font-bold text-navy-800">{terminal.store}</p>
          <p className="mt-0.5 flex items-center justify-center gap-[0.4375rem] text-xs text-ink-500">
            {terminal.terminal}
            <span
              className={cn(
                "size-[0.4375rem] rounded-full",
                online ? "bg-money-500 ring-[3px] ring-money-500/20" : "bg-ink-400",
              )}
              aria-hidden
            />
            {online ? "Online" : "Offline"}
          </p>
        </div>
      )}

      <div className="text-center">
        <h2 className="text-lg font-semibold text-ink-900">Staff sign in</h2>
        <p className="mt-0.5 text-sm text-ink-500">
          {roster.length > 0 ? "Choose your name and enter your PIN" : "Enter your staff code and PIN"}
        </p>
        {!online && (
          <p className="mt-2 rounded-control bg-warn-50 px-3 py-2 text-xs text-warn-700">
            No signal. Anyone who has signed in on this till with signal in the last 30 days can
            still sign in.
          </p>
        )}
      </div>

      {/* ── Profiles, when the terminal knows who works here ────────────── */}
      {roster.length > 0 ? (
        <div
          role="radiogroup"
          aria-label="Choose your profile"
          className="grid grid-cols-3 gap-2"
        >
          {roster.map((person) => {
            const chosen = person.staff_code === staffCode;
            return (
              <button
                key={person.staff_code}
                type="button"
                role="radio"
                aria-checked={chosen}
                onClick={() => {
                  setStaffCode(person.staff_code);
                  setPin("");
                }}
                className={cn(
                  "flex flex-col items-center gap-1.5 rounded-control border px-2 py-3 transition-colors",
                  chosen
                    ? "border-brand-500 bg-brand-50 ring-1 ring-brand-500"
                    : "border-line bg-surface hover:bg-canvas",
                )}
              >
                <span
                  className={cn(
                    "grid size-9 place-items-center rounded-full",
                    chosen ? "bg-brand-500 text-white" : "bg-canvas text-ink-500",
                  )}
                  aria-hidden
                >
                  <UserRound className="size-5" strokeWidth={2} />
                </span>
                <span className="w-full truncate text-sm font-medium text-ink-900">
                  {person.name}
                </span>
                {person.role && (
                  <span className="w-full truncate text-[0.6875rem] leading-tight text-ink-500">
                    {person.role}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      ) : (
        <div>
          <label htmlFor="staff-code" className="mb-1.5 block text-sm font-medium text-ink-900">
            Staff code
          </label>
          <input
            id="staff-code"
            value={staffCode}
            onChange={(event) => setStaffCode(event.target.value.toUpperCase())}
            autoComplete="off"
            autoCapitalize="characters"
            spellCheck={false}
            placeholder="NOMSA"
            className="h-touch-lg w-full rounded-control border border-line bg-surface px-3 text-center text-lg font-semibold uppercase tracking-wide text-ink-900 outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20"
          />
        </div>
      )}

      <div>
        {/* Not "4-digit": a PIN here is four to six, and telling somebody with
            six that they need four is how they burn through a lockout. */}
        <span className="mb-2 block text-center text-sm font-medium text-ink-900">
          Enter your PIN
        </span>
        <div className="mt-2.5 flex justify-center gap-[1.375rem]">
          {Array.from({ length: slots }, (_, index) => (
            <span
              key={index}
              className={cn(
                "size-[0.9375rem] rounded-full transition-all",
                index < pin.length
                  ? "scale-110 bg-navy-800 shadow-card"
                  : "bg-line",
              )}
            />
          ))}
        </div>
        {/* The dots are decorative; this is the accessible statement of progress. */}
        <span className="sr-only" aria-live="polite">
          {pin.length} of {PIN_MIN_LENGTH} to {PIN_MAX_LENGTH} digits entered
        </span>
      </div>

      <AuthError message={error} />

      <div className="grid grid-cols-3 gap-2">
        {KEYS.map((key, index) => (
          // Keyed by position, not by value: delete appears twice, and keying by
          // value would give two siblings the same key — React then reuses the
          // wrong node and the second one stops responding.
          <button
            key={`${key}-${index}`}
            type="button"
            onClick={() => press(key)}
            disabled={busy}
            aria-label={
              key === "backspace"
                ? "Delete last digit"
                : key === "clear"
                  ? "Clear all digits"
                  : key
            }
            className={cn(
              "grid h-11 place-items-center rounded-[0.5625rem] border text-[1.0625rem] font-semibold",
              "border-line/70 bg-canvas transition-colors active:translate-y-px",
              key === "backspace" || key === "clear"
                ? "text-ink-500 hover:bg-line/60"
                : "text-ink-900 hover:bg-line/60",
              "disabled:opacity-50",
            )}
          >
            {key === "backspace" || key === "clear" ? (
              <Delete
                className={cn("size-5", key === "clear" && "-scale-x-100")}
                aria-hidden
              />
            ) : (
              key
            )}
          </button>
        ))}
      </div>

      <Button
        variant="primary"
        size="pos"
        fullWidth
        loading={busy}
        disabled={!complete}
        onClick={() => void submit()}
      >
        {/* The arrow trails the label, as designed. `icon` is a *leading* slot
            on Button and stays that way — most buttons want it there. */}
        Open POS
        <ArrowRight className="ml-1 size-5" aria-hidden />
      </Button>

      {/* ── The two things somebody stuck at this screen actually needs ──── */}
      <div className="flex items-center justify-center gap-2 border-t border-line pt-4 text-sm">
        <button
          type="button"
          onClick={() => setForgot(true)}
          className="inline-flex items-center gap-1.5 px-2 py-1 text-ink-500 hover:text-ink-900"
        >
          <Lock className="size-3.5" aria-hidden />
          Forgot PIN?
        </button>
        <span className="text-line" aria-hidden>
          |
        </span>
        <span className="inline-flex items-center gap-1.5 px-2 py-1 text-ink-400">
          <Smartphone className="size-3.5" aria-hidden />
          Use mobile login
        </span>
      </div>

      {forgot && (
        <p className="rounded-control bg-canvas px-3 py-2 text-center text-sm text-ink-600">
          A manager can reset it from Settings ▸ Users. Only somebody who already
          holds that permission can — which is the point of a PIN.
        </p>
      )}

      {/* Quiet reassurance, as in the design. The sync line is the one a
          shopkeeper actually reads: it answers "did yesterday's takings
          reach head office" without them opening anything. */}
      <p className="flex items-center justify-center gap-2 text-center text-xs text-ink-400">
        <ShieldCheck className="size-3.5" aria-hidden />
        Secure access
        <span aria-hidden>·</span>
        Last sync: {lastSync}
      </p>
    </div>
  );
}
